import { DateTime } from 'luxon'
import { useEffect, useMemo, useState } from 'react'

import { getFetchError, getIsFetchingOrNotStarted } from '@/reducers/fetch'
import {
  fetchUserImportantDatesIfNeeded,
  FETCH_USER_IMPORTANT_DATES_KEY,
} from './userImportantDates.slice'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { getUserImportantDatesBetween } from './userImportantDates.selectors'

import { ImportantDateList } from './components/ImportantDateList'
import {
  Tooltip,
  Button,
  Card,
  Dialog,
  Text,
  CardColors,
  Icon,
  Loader,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  faQuestionCircle,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/pro-regular-svg-icons'
const NUMBER_ITEMS_SHOWN_IN_CARD = 3
const NUMBER_UPCOMING_MONTHS_IN_MODAL = 12

export const UserImportantDatesCard = () => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const [modalOpen, setModalOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(true)

  const dateRange = useMemo(
    () => ({
      startDateParam: DateTime.now(),
      endDateParam: DateTime.now()
        .plus({ months: NUMBER_UPCOMING_MONTHS_IN_MODAL })
        .endOf('day'),
    }),
    []
  )

  useEffect(() => {
    dispatch(fetchUserImportantDatesIfNeeded())
  }, [dispatch])

  const upcomingUserImportantDates = useReselector(
    getUserImportantDatesBetween,
    {
      startDateParam: dateRange.startDateParam,
      endDateParam: dateRange.endDateParam,
      count: NUMBER_ITEMS_SHOWN_IN_CARD,
    }
  )

  const allUserImportantDates = useReselector(getUserImportantDatesBetween, {
    startDateParam: dateRange.startDateParam,
    endDateParam: dateRange.endDateParam,
    count: null,
  })

  const isFetchingOrNotStarted = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_USER_IMPORTANT_DATES_KEY
  )

  const fetchError = useReselector(
    getFetchError,
    FETCH_USER_IMPORTANT_DATES_KEY
  )

  return (
    <Card color={CardColors.LEVEL_1}>
      {/* Header Row */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <Text variant={TypographyStyles.H2}>Important Dates</Text>

        <div className="hidden md:flex items-center gap-4">
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Icon icon={faQuestionCircle} className="cursor-pointer" />
            </Tooltip.Trigger>
            <Tooltip.Content>
              State-specific deadlines are not currently included.
            </Tooltip.Content>
          </Tooltip>

          <Icon
            icon={isCollapsed ? faChevronDown : faChevronUp}
            className="cursor-pointer"
            onClick={() => {
              track(
                `${isCollapsed ? 'Expanded' : 'Collapsed'} Important Dates card`
              )
              setIsCollapsed(!isCollapsed)
            }}
          />
        </div>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="mt-4 space-y-4">
          {fetchError && (
            <Text>
              There was an error loading this card. Please refresh the page to
              try again.
            </Text>
          )}

          {isFetchingOrNotStarted && <Loader active inline />}

          {!isFetchingOrNotStarted && !fetchError && (
            <ImportantDateList
              groupItemsByMonth={false}
              indicateUnreadItems
              userImportantDates={upcomingUserImportantDates}
            />
          )}

          <div>
            {allUserImportantDates.length === 0 ? (
              <Text>No upcoming important dates yet!</Text>
            ) : (
              <Button variant="link" onClick={() => setModalOpen(true)}>
                View All
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Modal for all dates */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <Dialog.Content className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <Dialog.Title>Upcoming Dates</Dialog.Title>

          <div className="space-y-4 mt-4">
            <Text>
              Below are important dates for the next twelve months.
              State-specific deadlines aren&apos;t currently available.
            </Text>

            <ImportantDateList
              userImportantDates={allUserImportantDates}
              indicateUnreadItems={false}
              groupItemsByMonth
            />
          </div>
        </Dialog.Content>
      </Dialog>
    </Card>
  )
}
